import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, ChevronRight } from 'lucide-react';
import { SendError, sendErrorMessageKey } from '../../errors/sendError';
import { useTranslation } from '../../i18n/hooks/useTranslation';

interface SendErrorBannerProps {
  error: SendError;
  /** Appelé quand le banner doit disparaître (auto-dismiss ou action utilisateur). */
  onDismiss: () => void;
  /** Délai d'auto-effacement en ms. Mettre 0 pour désactiver. Défaut : 10000. */
  autoDismissMs?: number;
  /** Message clair à la place de celui dérivé de `error.kind`. */
  messageKey?: string;
  testId?: string;
}

/**
 * Banner d'erreur d'envoi présenté à l'arbitre.
 *
 * Affiche un message clair (traduit depuis `error.kind`) et un panneau repliable
 * "Détails techniques" contenant le payload brut, utile pour le support terrain.
 *
 * Auto-effacement après `autoDismissMs`, MAIS le timer est figé dès que l'arbitre
 * déplie les détails (interaction = intention de lire, on ne lui arrache pas le
 * contenu sous les yeux).
 */
export function SendErrorBanner({ error, onDismiss, autoDismissMs = 10000, messageKey, testId }: SendErrorBannerProps) {
  const { t } = useTranslation();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    // Pas d'auto-effacement si désactivé ou si l'arbitre a ouvert les détails.
    if (autoDismissMs <= 0 || detailsOpen) return;

    const timer = setTimeout(() => onDismissRef.current(), autoDismissMs);
    return () => clearTimeout(timer);
    // Se réarme à chaque nouvelle erreur ; s'annule dès que detailsOpen passe à true.
  }, [error, autoDismissMs, detailsOpen]);

  const message = t(messageKey ?? sendErrorMessageKey(error.kind));

  return (
    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg" role="alert" data-testid={testId}>
      <div className="flex items-start gap-2">
        <AlertCircle size={18} className="text-red-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-red-800 text-sm font-medium">{message}</div>

          <button
            type="button"
            onClick={() => setDetailsOpen((open) => !open)}
            className="mt-1 inline-flex items-center gap-1 text-red-600 text-xs hover:underline"
            aria-expanded={detailsOpen}
          >
            <ChevronRight
              size={14}
              className={`transition-transform ${detailsOpen ? 'rotate-90' : ''}`}
            />
            {t('errors.send.technicalDetails')}
          </button>

          {detailsOpen && (
            <pre className="mt-2 p-2 bg-red-100 text-red-700 text-xs rounded whitespace-pre-wrap break-words">
              {error.httpStatus ? `HTTP ${error.httpStatus}\n` : ''}
              {error.technical}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}
